import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { SuspenseQueryWith } from '@/utils/types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokenWithBalance } from '@/domain/common/types'
import { getTokenBySymbol, TokenSymbolEnum } from '@/utils/tokens'
import { hyperEVM } from '@/config/chain/constants'
import { HOST } from '@/config/consts'

interface LifiTokenResponse {
  tokens: {
    [chainId: string]: {
      chainId: number
      address: CheckedAddress
      symbol: TokenSymbol
      decimals: number
      name: string
      logoURI?: string
      priceUSD?: string
    }[]
  }
}

const MANUAL_SWAP_ASSETS = [
  {
    token: new Token({
      ...getTokenBySymbol(TokenSymbolEnum.CMETH),
      unitPriceUsd: '0',
    }),
    balance: NormalizedUnitNumber(0),
  },
]

function getLifiTokensUrl(): string {
  if (typeof window !== 'undefined') {
    return '/api/lifi/tokens'
  }

  return `${HOST}/api/lifi/tokens`
}

export function getSwapTokens() {
  return queryOptions<TokenWithBalance[]>({
    queryKey: ['swap-tokens', 'lifi'],
    queryFn: async () => {
      try {
        const res = await fetch(getLifiTokensUrl(), {
          headers: {
            Accept: 'application/json',
          },
        })
        if (!res.ok) {
          throw new Error('Failed to fetch tokens')
        }
        const data = (await res.json()) as LifiTokenResponse
        const hyperEVMTokens = data.tokens[hyperEVM.id.toString()]

        if (!hyperEVMTokens) {
          throw new Error('No tokens found for hyperEVM')
        }

        const formattedTokens: TokenWithBalance[] = hyperEVMTokens.map((token) => ({
          token: new Token({
            address: token.address,
            decimals: token.decimals,
            name: token.name,
            symbol: token.symbol,
            unitPriceUsd: token.priceUSD ?? '0',
          }),
          balance: NormalizedUnitNumber(0),
        }))
        return [...formattedTokens, ...MANUAL_SWAP_ASSETS]
      } catch (error) {
        console.error(error)
        return []
      }
    },
  })
}

export type UseGetSwapTokensResult = SuspenseQueryWith<{ tokens: TokenWithBalance[] }>

export function useGetSwapTokens(): UseGetSwapTokensResult {
  const response = useSuspenseQuery({
    ...getSwapTokens(),
  })

  return {
    ...response,
    tokens: response.data,
  }
}
